import { Box, Flex, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton } from '../../../components/ActionButton'
import { DetailSheet } from '../../../components/DetailSheet'
import { FormModal } from '../../../components/FormModal'
import { LoadingBlock } from '../../../components/Loading'
import { SectionTitle } from '../../../components/SectionCard'
import { formatDateTime, formatFileSize, useAppToast, useDownload } from '../../../helpers'
import { TestimonialVideoService } from '../../../services/TestimonialVideo/TestimonialVideoService'
import { ITestimonialVideoSubmission } from '../../../services/TestimonialVideo/types'
import { formatAddress } from './consts'

interface ITestimonialVideoViewModalProps {
  id: number | null
  onClose: () => void
}

export default function TestimonialVideoViewModal({ id, onClose }: ITestimonialVideoViewModalProps) {
  const service = new TestimonialVideoService()
  const toast = useAppToast()
  const { isDownloading, download } = useDownload()
  const [submission, setSubmission] = useState<ITestimonialVideoSubmission | null>(null)
  const [loading, setLoading] = useState(true)

  // Same render-time adjustment as SalesReceiptViewModal: the skeleton shows on the very
  // first frame after opening, instead of the previous record.
  const [openedId, setOpenedId] = useState(id)
  if (id !== openedId) {
    setOpenedId(id)
    if (id !== null) setLoading(true)
  }

  useEffect(() => {
    if (id === null) return

    const fetch = async () => {
      try {
        setSubmission(await service.get(id))
      } catch (e) {
        toast.error('Erro ao carregar o vídeo de depoimento', e)
        onClose()
      } finally {
        setLoading(false)
      }
    }
    fetch()
  }, [id])

  return (
    <FormModal isOpen={id !== null} onClose={onClose} title="Vídeo de Depoimento" size="wide">
      {loading || !submission ? (
        <LoadingBlock />
      ) : (
        <>
          {submission.video_url && (
            <Box borderRadius="md" overflow="hidden" bg="black">
              <video
                key={submission.video_url}
                src={submission.video_url}
                controls
                playsInline
                preload="metadata"
                style={{ width: '100%', maxHeight: '420px', display: 'block' }}
              />
            </Box>
          )}

          <Flex justify="flex-end" mt={2} mb={4}>
            <ActionButton
              action="download"
              label="Baixar vídeo"
              showLabel
              isLoading={isDownloading}
              onClick={() => download(() => service.download(submission.id), 'Erro ao baixar o vídeo')}
            />
          </Flex>

          <DetailSheet
            fields={[
              { label: 'Nome', value: submission.name },
              { label: 'E-mail', value: submission.email },
              { label: 'Endereço', value: formatAddress(submission) },
              { label: 'Curso', value: submission.course_name },
              { label: 'Modalidade', value: submission.modality_label },
              {
                label: 'Arquivo',
                value: `${submission.video_original_name} (${formatFileSize(submission.video_size)})`
              },
              { label: 'Enviado em', value: formatDateTime(submission.created_at) }
            ]}
          />

          <SectionTitle variant="mini" mt={6}>
            Aceite do termo
          </SectionTitle>
          <DetailSheet
            fields={[
              { label: 'Aceito em', value: formatDateTime(submission.consent_accepted_at, { showSeconds: true }) },
              { label: 'IP', value: submission.consent_ip },
              { label: 'Navegador', value: submission.consent_user_agent }
            ]}
          />
          <Text variant="label" mt={4}>
            Texto aceito
          </Text>
          <Text fontSize="sm" mt={1} whiteSpace="pre-line" textAlign="justify">
            {submission.consent_text}
          </Text>
        </>
      )}
    </FormModal>
  )
}
